<template>
    <div class="w-full">

        <!-- Selector de plantilla PDF -->
        <div class="graph-select-card">
            <div class="graph-select-header">
                <div>
                    <p class="graph-select-title">Seleccionar gráfica</p>
                    <p class="graph-select-caption">Elige la plantilla que mejor represente al paciente.</p>
                </div>
                <span class="graph-select-chip">{{ graphTitle || 'Crecimiento' }}</span>
            </div>
            <label class="sr-only" for="pdfSelect">Seleccionar gráfica</label>
            <div class="graph-select-control">
                <select id="pdfSelect" v-model="selectedPdf" class="graph-select-input" @change="handlePdfChange">
                    <option value="">Seleccionar</option>
                    <option value="boy36-m">Niño 0–36&nbsp;m</option>
                    <option value="boy-2a20">Niño 2–20&nbsp;a</option>
                    <option value="boy-2a20-imc">Niño 2–20 a&nbsp;IMC</option>
                    <option value="boy-2a20-cabeza">Niño 0–36&nbsp;m Cabeza</option>
                    <option value="girl36-m">Niña 0–36&nbsp;m</option>
                    <option value="girl-2a20">Niña 2–20&nbsp;a</option>
                    <option value="girl-2a20-imc">Niña 2–20 a&nbsp;IMC</option>
                    <option value="girl-2a20-cabeza">Niña 0–36&nbsp;m Cabeza</option>
                </select>
            </div>
        </div>

        <div v-if="isGraphSelected">
            <canvas ref="canvas" class="w-full max-w-full" />
        </div>
        <div v-else class="graph-placeholder">
            <i class="fas fa-chart-area"></i>
            <p>Selecciona una plantilla para visualizar la gráfica de crecimiento.</p>
        </div>
    </div>
</template>

<script setup>
import { ref, watch, onMounted, computed } from 'vue'
import { GlobalWorkerOptions, getDocument } from 'pdfjs-dist'
import api from '@/services/api'

/* ───────── CONFIG GLOBAL ───────── */
GlobalWorkerOptions.workerSrc = '/js/pdf.worker.min.mjs'
const SCALE = 1.5
const DOT_RADIUS = 6
const DOT_COLOR = {
    height: '#8B5CF6', // púrpura oscuro
    weight: '#10B981' // verde intenso
}

const SHOW_GRID = true

/* ---------- PROPS ---------- */
const props = defineProps({ patientId: Number })

/* ---------- REFS ---------- */
const canvas = ref(null)
const data = ref([])          // crecimiento
const selectedPdf = ref('')
const pdfUrl = ref('')
const graphTitle = ref('')
const isGraphSelected = computed(() => !!selectedPdf.value)

/* ---------- CARGA PDF ---------- */
let loadTask = null
let debounce = null

/* ---------- META POR PLANTILLA ---------- */
const pdfOptions = {
    'boy36-m': {
        title: 'Niño 0–36 m',
        file: '/pdfs/boy36-m.pdf',
        age: [0, 36],
        weight: [2, 18],
        height: [37, 107],
        grid_weight: { x: 110, y: 223, w: 383, h: 480 },  // peso
        grid_height: { x: 110, y: 90, w: 383, h: 420 } // estatura

    },
    'boy-2a20': {
        title: 'Niño 2–20 a',
        file: '/pdfs/boy-2a20.pdf',
        age: [2, 20],
        weight: [10, 110],
        height: [75, 197],
        grid_weight: { x: 110, y: 300, w: 385, h: 405 }, // peso
        grid_height: { x: 110, y: 90, w: 385, h: 494 }  // estatura
    },
    'boy-2a20-imc': {
        title: 'Niño 2–20 a IMC',
        file: '/pdfs/boy-2a20-imc.pdf',
        age: [2, 20],
        weight: [12, 35],  // para IMC
        grid_height: { x: 83, y: 136, w: 440, h: 524 } // ← duplicado// solo usaremos este
    },
    'boy-2a20-cabeza': {
        title: 'Niño 2–20 a IMC',
        file: '/pdfs/boy36-m-cabeza.pdf',
        age: [2, 20],
        weight: [12, 35],  // para IMC
        grid_height: { x: 83, y: 136, w: 440, h: 524 } // ← duplicado// solo usaremos este
    },
    'girl36-m': {
        title: 'Niña 0–36 m',
        file: '/pdfs/girl36-m.pdf',
        age: [0, 36],
        weight: [2, 18],
        height: [37, 107],
        grid_weight: { x: 110, y: 223, w: 383, h: 480 },
        grid_height: { x: 110, y: 90, w: 383, h: 420 }
    },
    'girl-2a20': {
        title: 'Niña 2–20 a',
        file: '/pdfs/girl-2a20.pdf',
        age: [2, 20],
        weight: [10, 110],
        height: [75, 197],
        grid_weight: { x: 110, y: 300, w: 385, h: 405 }, // peso
        grid_height: { x: 110, y: 90, w: 385, h: 494 }  // estatura
    },
    'girl-2a20-imc': {
        title: 'Niña 2–20 a IMC',
        file: '/pdfs/girl-2a20-imc.pdf',
        age: [2, 20],
        height: [12, 35],
        grid_height: { x: 83, y: 136, w: 440, h: 524 }
    },
    'girl-2a20-cabeza': {
        title: 'Niña 2–20 a IMC',
        file: '/pdfs/girl36-m-cabeza.pdf',
        age: [2, 20],
        height: [12, 35],
        grid_height: { x: 83, y: 136, w: 440, h: 524 }
    }
}

const activeMeta = () => pdfOptions[selectedPdf.value]

/* ---------- TRANSFORMACIONES ---------- */
function pointsForChart() {
    const meta = activeMeta()
    const isYears = selectedPdf.value.includes('2a20')
    const min = meta.age[0]
    const max = meta.age[1]

    return data.value
        .map(p => {
            const age = isYears ? +(p.age_in_months / 12).toFixed(2) : p.age_in_months
            const bmi = +(p.weight / ((p.height / 100) ** 2)).toFixed(2)
            return { age, weight: p.weight, height: p.height, bmi }
        })
        .filter(p => p.age >= min && p.age <= max)
}


function mapPoint(pt, kind) {
    const meta = activeMeta()
    let grid, rango, valor

    if (kind === 'bmi') {
        grid = meta.grid_height  // usamos grid_height para IMC
        rango = [12, 34] // Rango típico de IMC (ajústalo si es necesario)
        valor = pt.bmi
    } else {
        grid = kind === 'weight' ? meta.grid_weight : meta.grid_height
        rango = kind === 'weight' ? meta.weight : meta.height
        valor = kind === 'weight' ? pt.weight : pt.height
    }

    const aPct = (pt.age - meta.age[0]) / (meta.age[1] - meta.age[0])
    const vPct = (valor - rango[0]) / (rango[1] - rango[0])

    return {
        x: (grid.x + grid.w * aPct) * SCALE,
        y: (grid.y + grid.h * (1 - vPct)) * SCALE
    }
}

/* ---------- DIBUJO ---------- */
function drawSmoothLine(ctx, pts, color) {
    if (pts.length < 2) return
    ctx.beginPath()
    ctx.moveTo(pts[0].x, pts[0].y)
    for (let i = 0; i < pts.length - 1; i++) {
        const p0 = pts[i === 0 ? i : i - 1]
        const p1 = pts[i], p2 = pts[i + 1]
        const p3 = pts[i + 2 > pts.length - 1 ? pts.length - 1 : i + 2]
        const t = 0.2                         // tensión Catmull‑Rom
        const cp1x = p1.x + (p2.x - p0.x) / 6 * t
        const cp1y = p1.y + (p2.y - p0.y) / 6 * t
        const cp2x = p2.x - (p3.x - p1.x) / 6 * t
        const cp2y = p2.y - (p3.y - p1.y) / 6 * t
        ctx.bezierCurveTo(cp1x, cp1y, cp2x, cp2y, p2.x, p2.y)
    }
    ctx.strokeStyle = color
    ctx.lineWidth = 2
    ctx.stroke()
}

function draw(ctx) {
    const rows = pointsForChart().sort((a, b) => a.age - b.age)

    if (selectedPdf.value.includes('imc')) {
        // Solo IMC
        const ptsBMI = rows.map(p => ({ ...p, ...mapPoint(p, 'bmi') }))
        drawSmoothLine(ctx, ptsBMI, '#0EA5E9') // azul claro
        ctx.fillStyle = '#0EA5E9'
        ptsBMI.forEach(p => { ctx.beginPath(); ctx.arc(p.x, p.y, DOT_RADIUS, 0, Math.PI * 2); ctx.fill() })

        // Leyenda
        ctx.font = 'bold 14px sans-serif'
        ctx.fillStyle = '#0EA5E9'
        ctx.beginPath()
        ctx.arc(40, 20, 6, 0, Math.PI * 2)
        ctx.fill()
        ctx.fillText('IMC', 55, 25)
        return
    }

    // Normal: altura y peso
    const ptsH = rows.map(p => ({ ...p, ...mapPoint(p, 'height') }))
    drawSmoothLine(ctx, ptsH, DOT_COLOR.height)
    ctx.fillStyle = DOT_COLOR.height
    ptsH.forEach(p => { ctx.beginPath(); ctx.arc(p.x, p.y, DOT_RADIUS, 0, Math.PI * 2); ctx.fill() })

    const ptsW = rows.map(p => ({ ...p, ...mapPoint(p, 'weight') }))
    drawSmoothLine(ctx, ptsW, DOT_COLOR.weight)
    ctx.fillStyle = DOT_COLOR.weight
    ptsW.forEach(p => { ctx.beginPath(); ctx.arc(p.x, p.y, DOT_RADIUS, 0, Math.PI * 2); ctx.fill() })

    // Leyenda
    ctx.font = 'bold 14px sans-serif'
    ctx.fillStyle = DOT_COLOR.height
    ctx.beginPath()
    ctx.arc(40, 20, 6, 0, Math.PI * 2)
    ctx.fill()
    ctx.fillText('Estatura', 55, 25)

    ctx.fillStyle = DOT_COLOR.weight
    ctx.beginPath()
    ctx.arc(140, 20, 6, 0, Math.PI * 2)
    ctx.fill()
    ctx.fillText('Peso', 155, 25)
}

function drawDebugGrid(ctx) {
    if (!SHOW_GRID) return

    const meta = activeMeta()

    const grids = []

    if (meta.grid_weight) {
        grids.push({ kind: 'weight', grid: meta.grid_weight, color: 'rgba(0,0,255,0.6)' }) // azul
    }
    if (meta.grid_height) {
        grids.push({ kind: 'height', grid: meta.grid_height, color: 'rgba(255,0,0,0.6)' }) // rojo
    }

    grids.forEach(({ grid: g, color }) => {
        ctx.save()
        ctx.strokeStyle = color
        ctx.lineWidth = 2
        ctx.strokeRect(g.x * SCALE, g.y * SCALE, g.w * SCALE, g.h * SCALE)
        ctx.restore()
    })
}

function clearCanvas() {
    if (!canvas.value) return
    const ctx = canvas.value.getContext('2d')
    if (!ctx) return
    ctx.clearRect(0, 0, canvas.value.width || 0, canvas.value.height || 0)
}

/* ---------- RENDER ---------- */
async function render() {
    if (!canvas.value || !pdfUrl.value) return
    if (loadTask) {
        try { await loadTask.destroy() } catch {
            console.error('Error al destruir la tarea de carga del PDF')
        }
    }
    loadTask = getDocument(pdfUrl.value)
    const pdf = await loadTask.promise
    const page = await pdf.getPage(1)
    const vp = page.getViewport({ scale: SCALE })
    const ctx = canvas.value.getContext('2d')

    canvas.value.width = vp.width
    canvas.value.height = vp.height
    ctx.clearRect(0, 0, vp.width, vp.height)

    await page.render({ canvasContext: ctx, viewport: vp }).promise
    draw(ctx)
    drawDebugGrid(ctx)
}

/* ---------- EVENTOS ---------- */
function handlePdfChange() {
    if (!selectedPdf.value) {
        graphTitle.value = ''
        pdfUrl.value = ''
        clearTimeout(debounce)
        clearCanvas()
        return
    }
    const meta = activeMeta()
    if (!meta) {
        console.warn('Plantilla no definida:', selectedPdf.value)
        return
    }
    pdfUrl.value = meta.file
    graphTitle.value = meta.title
    clearTimeout(debounce)
    debounce = setTimeout(render, 80)
}

/* ---------- INIT ---------- */
async function loadData() {
    const { data: rows } = await api.get(`/pacientes/${props.patientId}/growth-data`)
    data.value = rows.map(r => ({
        age_in_months: +r.age,
        weight: +r.weight,
        height: +r.height
    }))
    if (!data.value.length) return
}

watch(selectedPdf, (value) => {
    if (value) {
        handlePdfChange()
    } else {
        graphTitle.value = ''
        pdfUrl.value = ''
        clearCanvas()
    }
})   // ya no observamos 'mode'
onMounted(loadData)
</script>

<style scoped>
.graph-select-card {
    background: linear-gradient(135deg, rgba(79, 70, 229, 0.1), rgba(14, 165, 233, 0.08));
    border: 1px solid #dbeafe;
    border-radius: 16px;
    padding: 1.25rem 1.5rem;
    margin-bottom: 1.5rem;
    box-shadow: 0 12px 24px rgba(15, 23, 42, 0.08);
}

.graph-select-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 1rem;
}

.graph-select-title {
    margin: 0;
    font-weight: 600;
    color: #0f172a;
    font-size: 1rem;
}

.graph-select-caption {
    margin: 0.25rem 0 0;
    font-size: 0.9rem;
    color: #475569;
}

.graph-select-chip {
    background: rgba(79, 70, 229, 0.15);
    color: #4338ca;
    padding: 0.2rem 0.9rem;
    border-radius: 999px;
    font-size: 0.85rem;
    font-weight: 600;
    letter-spacing: 0.03em;
}

.graph-select-control {
    margin-top: 1rem;
}

.graph-select-input {
    width: 100%;
    padding: 0.65rem 0.85rem;
    border: 1px solid #c7d2fe;
    border-radius: 10px;
    background: #fff;
    font-size: 0.95rem;
    font-weight: 500;
    color: #1e293b;
    transition: border-color 0.2s ease, box-shadow 0.2s ease;
    appearance: none;
    background-image: url("data:image/svg+xml,%3Csvg width='14' height='9' viewBox='0 0 14 9' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M13 1.5L7 7.5L1 1.5' stroke='%234338CA' stroke-width='1.8' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: calc(100% - 12px) center;
    background-size: 14px;
}

.graph-select-input:focus {
    outline: none;
    border-color: #6366f1;
    box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.25);
}

.graph-placeholder {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 2rem 1rem;
    border: 1px dashed #cbd5f5;
    border-radius: 16px;
    color: #64748b;
    background: #f8fafc;
    gap: 0.5rem;
    text-align: center;
}

.graph-placeholder i {
    font-size: 1.75rem;
    color: #94a3b8;
}

.graph-heading {
    font-size: 1.35rem;
    font-weight: 700;
    color: #0f172a;
    margin-bottom: 1rem;
}

.sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    border: 0;
}

canvas {
    border: 1px solid #e2e8f0;
    border-radius: 16px;
    background: #fff;
    box-shadow: 0 10px 15px rgba(15, 23, 42, 0.08);
}
</style>
